<template>
  <div
    v-if="slides.length"
    :class="containerClasses"
    data-ui="mc-carousel"
  >
    <div class="mc-carousel__header">
      <div class="mc-carousel__headings">
        <cdr-text
          tag="h2"
          class="mc-carousel__heading"
        >
          {{ heading }}
        </cdr-text>
        <cdr-text class="mc-carousel__subheading">
          {{ subheading }}
        </cdr-text>
      </div>
      <cdr-link
        :aria-label="ariaCtaText"
        :data-analytics-id="analyticsId"
        :href="ctaHref"
        class="mc-carousel__cta"
        modifier="standalone"
      >
        {{ computedCtaText }}
      </cdr-link>
    </div>
    <div
      v-if="!useCarousel"
      class="thumbnail-nav"
      data-ui="mobile-carousel"
    >
      <ul
        class="thumbnail-nav__track"
        tabindex="0"
      >
        <li
          v-for="{ key, props } in slides"
          :key="key"
        >
          <collection-product
            v-bind="props"
            :heading="heading"
            :lazy-load="lazyLoad"
          />
        </li>
      </ul>
    </div>
    <carousel-component
      v-else
      :id="id"
      :description="ariaDescription"
      :enable-boundary-bounce="true"
      :slides="slides"
      :slides-to-show="4"
      :slides-to-scroll="3"
      data-ui="desktop-carousel"
      height="auto"
      @after-change.once="hasScrolled = true"
    >
      <!-- eslint-disable-next-line -->
      <template #slide="{ originalIndex, handleLoad, handleSlideEvent, ...props }">
        <collection-carousel-product
          v-bind="props"
          :heading="heading"
          :lazy-load="lazyLoad"
        />
      </template>
    </carousel-component>
  </div>
</template>

<script>
import { CarouselComponent } from '@rei/carousel';
import { CdrLink, CdrText } from '@rei/cedar';
import { CdrBreakpointMd } from '@rei/cdr-tokens';

import CollectionCarouselProduct from './CollectionCarouselProduct.vue';

export default {
  name: 'CollectionCarousel',
  components: {
    CarouselComponent,
    CollectionCarouselProduct,
    CdrLink,
    CdrText,
  },
  props: {
    ctaHref: {
      type: String,
      required: true,
    },
    ctaText: {
      type: String,
      required: true,
    },
    heading: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      required: true,
    },
    lazyLoad: {
      type: Boolean,
      required: false,
      default: false,
    },
    screenWidth: {
      type: Number,
      required: true,
    },
    slides: {
      type: Array,
      required: true,
    },
    subheading: {
      type: String,
      required: true,
    },
    total: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      hasScrolled: false,
    };
  },
  computed: {
    ariaCtaText() {
      const { ctaText, heading, slides } = this;
      return `${ctaText} ${slides.length} ${heading} products`;
    },
    ariaDescription() {
      const { heading } = this;
      return `a collection of "${heading}" products`;
    },
    analyticsId() {
      // Early access_View all
      const { ctaText, heading } = this;
      return `${heading}_${ctaText}`;
    },
    computedCtaText() {
      const { ctaText, total } = this;
      return `${ctaText} (${total})`;
    },
    containerClasses() {
      return ['mc-carousel', { 'mc-carousel--hide-left-arrow': !this.hasScrolled }];
    },
    useCarousel() {
      const { screenWidth } = this;
      return screenWidth > CdrBreakpointMd;
    },
  },
};

</script>

<style lang="scss">

.mc-carousel {
  margin-bottom: $cdr-space-two-x;

  @include cdr-md-mq-down {
    position: relative;
    height: 505px;

    .thumbnail-nav {
      position: relative;
    }
  }
  @include cdr-md-mq-up {
    @include cdr-container;

    .carousel-component {
      margin-left: -$cdr-space-one-x;
      margin-right: -$cdr-space-one-x;
    }

    margin-bottom: $cdr-space-four-x;
  }

  .carousel-component__arrow[disabled] {
    display: none;
  }

  .carousel-component__arrow {
    margin: auto;
  }
}

.mc-carousel--hide-left-arrow .carousel-component__arrow--left {
  visibility: hidden;
}

.mc-carousel__header {
  display: grid;
  grid-auto-flow: column;
  grid-gap: $cdr-space-half-x;
  padding: 0 $cdr-space-one-x;
  margin-bottom: $cdr-space-one-x;
  @include cdr-md-mq-up {
    padding: 0
  }
}

.mc-carousel__heading,
.mc-carousel__subheading {
  margin: 0;
}

.mc-carousel__heading {
  @include cdr-text-heading-sans-600;
  color: $cdr-color-text-emphasis;
  margin-bottom: $cdr-space-quarter-x;
}

.mc-carousel__subheading {
  @include cdr-text-subheading-sans-300;
}

.mc-carousel__cta {
  align-self: end;
  justify-self: end;
}

.thumbnail-nav {
  overflow: auto;
  padding-bottom: $cdr-space-half-x;
}

.thumbnail-nav__track {
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: $cdr-space-one-and-a-half-x;
  padding: 0 0 0 $cdr-space-one-x;
  margin: 0;
  list-style: none;
}

.thumbnail-nav__card {
  padding-left: $cdr-space-one-and-a-half-x;
  &:last-child {
    padding-right: $cdr-space-one-and-a-half-x;
  }
}

.thumbnail-nav__card-inner {
  display: flex;
  align-items: flex-end;
  position: relative;
  padding: $cdr-space-inset-one-x;
  min-width: 260px;
  height: 160px;
  border-radius: $cdr-radius-softer * 2;
  overflow: hidden;
}
</style>
